import * as merge from 'lodash/merge';

// If you have any questions about this file, message #dev-shared-widgets
const SpacingUnit = 6;
const SizingUnit = SpacingUnit;

export interface SemanticColors {
  White?: string;
  WhiteTransparent?: string;
  WhiteDisabled?: string;
  WhiteTransparentLine?: string;
  WhiteTransparentLineHover?: string;
  Black?: string;
  Transparent?: string;
  ActionLight?: string;
  ActionDark?: string;
  AttentionLight?: string;
  AttentionDark?: string;
  AttentionDarkDisabled?: string;
  Continuous?: string;
  ContinuousDark?: string;
  Discrete?: string;
  DiscreteDark?: string;
  GoLight?: string;
  GoDark?: string;
  GoDarkDisabled?: string;
  Shaded?: string;
  ApplicationBlue?: string;
  DesktopBlue?: string;
  ServerBlue?: string;
  OnlineBlue?: string;
  OpaqueGlass?: string;
  Focus?: string;
  L1?: string;
  L2?: string;
  L2_5?: string;
  L3?: string;
  L4?: string;
  L5?: string;
  L6?: string;
  L7?: string;
  L8?: string;
  L9?: string;
  L9Disabled?: string;
  F1?: string;
  F2?: string;
  F2_5?: string;
  F3?: string;
  F4?: string;
  F5?: string;
  F6?: string;
  F7?: string;
  F7Disabled?: string;
  F8?: string;
  F9?: string;
}

export interface Shadow {
  Elevated?: string;
  Standard?: string;
}

export interface Spacing {
  SmallSpace?: number;
  DefaultSpace?: number;
  MediumSpace?: number;
  LargeSpace?: number;
  ExtraLargeSpace?: number;
}

export interface Sizing {
  BaseUnit?: number;
  RowHeight?: number;
  ButtonMinWidth?: number;
  BorderWidth?: number;
  ButtonBorderWidth?: number;
  BorderRadius?: number;
  SelectionBorderWidth?: number;
  ListRowHeight?: number;
}

export interface FontSizes {
  XXLarge?: number;
  XLarge?: number;
  Large?: number;
  Default?: number;
  Small?: number;
  Tiny?: number;
}

export interface FontColors {
  PrimaryDark?: string;
  Primary?: string;
  PrimaryMedium?: string;
  PrimaryLight?: string;
  Secondary?: string;
  Disabled?: string;
  InvertedPrimary?: string;
  InvertedSecondary?: string;
  InvertedDisabled?: string;
}

export interface Typography {
  /**
   * @deprecated `font-family` should be inherited from the application-level stylesheet;
   * weight should be selected via the CSS `font-weight` property rather than a named face.
   */
  FontFamily?: string;
  Sizing?: FontSizes;
  Color?: FontColors;
}

export interface Opacity {
  ClearGlass?: number;
  Disabled?: number;
  OpaqueGlass?: number;
}

export interface Styles {
  Colors?: SemanticColors;
  Shadow?: Shadow;
  Spacing?: Spacing;
  Sizing?: Sizing;
  Typography?: Typography;
  Opacity?: Opacity;
}

// TODO: Semantic names - https://mytableau.tableaucorp.com/display/devft/Browser+Font+Reconciliation
const FontSizes: Required<FontSizes> = {
  XXLarge: 24,
  XLarge: 18,
  Large: 13,
  Default: 12,
  Small: 11,
  Tiny: 10
};

const FontColors: Required<FontColors> = {
  PrimaryDark: 'rgba(0, 0, 0, 0.9)',
  Primary: 'rgba(0, 0, 0, 0.8)',
  PrimaryMedium: 'rgba(0, 0, 0, 0.7)',
  PrimaryLight: 'rgba(0, 0, 0, 0.6)',
  Secondary: 'rgba(0, 0, 0, 0.56)',
  Disabled: 'rgba(0, 0, 0, 0.35)',
  InvertedPrimary: 'rgb(255, 255, 255)',
  InvertedSecondary: 'rgba(255, 255, 255, 0.96)',
  InvertedDisabled: 'rgba(255, 255, 255, 0.35)',
};

const GreyColors = {
  Grey250: 'rgb(250, 250, 250)',
  Grey245: 'rgb(245, 245, 245)',
  Grey235: 'rgb(235, 235, 235)',
  Grey230: 'rgb(230, 230, 230)',
  Grey225: 'rgb(225, 225, 225)',
  Grey212: 'rgb(212, 212, 212)',
  Grey203: 'rgb(203, 203, 203)',
  Grey180: 'rgb(180, 180, 180)',
  Grey180Disabled: 'rgba(180, 180, 180, 0.35)',
  Grey102: 'rgb(102, 102, 102)',
  Grey51: 'rgb(51, 51, 51)',
  Grey51Disabled: 'rgba(51, 51, 51, 0.2)',
};

const SemanticColors: Required<SemanticColors> = {
  White: 'rgb(255, 255, 255)',
  WhiteTransparent: 'rgba(255, 255, 255, 0.25)',
  WhiteDisabled: 'rgba(255, 255, 255, 0.35)',
  WhiteTransparentLine: 'rgba(255, 255, 255, 0.5)',
  WhiteTransparentLineHover: 'rgba(255, 255, 255, 0.8)',
  Black: 'rgb(0, 0, 0)',
  Transparent: 'rgba(0, 0, 0, 0)',
  ActionLight: 'rgb(235, 143, 80)',
  ActionDark: 'rgb(209, 107, 54)',
  AttentionLight: 'rgb(235, 68, 84)',
  AttentionDark: 'rgb(201, 58, 71)',
  AttentionDarkDisabled: 'rgba(201, 58, 71, 0.35)',
  Continuous: 'rgb(0, 177, 128)',
  ContinuousDark: 'rgb(0, 151, 104)',
  Discrete: 'rgb(73, 150, 178)',
  DiscreteDark: 'rgb(54, 126,156)',
  GoLight: 'rgb(45, 204, 151)',
  GoDark: 'rgb(47, 163, 125)',
  GoDarkDisabled: 'rgba(47, 163, 125, 0.35)',
  Shaded: 'rgba(0, 0, 0, 0.07)',
  ApplicationBlue: 'rgb(34, 38, 54)',
  DesktopBlue: 'rgb(53, 92, 128)',
  ServerBlue: 'rgb(42, 87, 117)',
  OnlineBlue: 'rgb(40, 94, 127)',
  OpaqueGlass: GreyColors.Grey245,
  Focus: 'rgb(52, 152, 219)',
  L1: GreyColors.Grey250,
  L2: GreyColors.Grey245,
  L2_5: GreyColors.Grey235,
  L3: GreyColors.Grey230,
  L4: GreyColors.Grey225,
  L5: GreyColors.Grey212,
  L6: GreyColors.Grey203,
  L7: GreyColors.Grey180,
  L8: GreyColors.Grey102,
  L9: GreyColors.Grey51,
  L9Disabled: GreyColors.Grey51Disabled,
  F1: GreyColors.Grey250,
  F2: GreyColors.Grey245,
  F2_5: GreyColors.Grey235,
  F3: GreyColors.Grey230,
  F4: GreyColors.Grey225,
  F5: GreyColors.Grey212,
  F6: GreyColors.Grey203,
  F7: GreyColors.Grey180,
  F7Disabled: GreyColors.Grey180Disabled,
  F8: GreyColors.Grey102,
  F9: GreyColors.Grey51
};

export type DeepRequired<T> = T extends {}
  ? { [K in keyof T]-?: DeepRequired<T[K]> }
  : NonNullable<T>;

export const TabStyles: DeepRequired<Styles> = {
  Colors: SemanticColors,
  Shadow: {
    Elevated: '0 2px 16px 0 rgba(51, 51, 51, 0.15)',
    Standard: '1px 1px 2px 0px rgba(0, 0, 0, 0.15)',
  },
  Spacing: {
    SmallSpace: SpacingUnit / 2,
    DefaultSpace: SpacingUnit,
    MediumSpace: SpacingUnit * 2,
    LargeSpace: SpacingUnit * 3,
    ExtraLargeSpace: SpacingUnit * 4,
  },
  Sizing: {
    BaseUnit: SizingUnit,
    RowHeight: 24,
    ButtonMinWidth: SizingUnit * 15,
    BorderWidth: 1,
    ButtonBorderWidth: 2,
    BorderRadius: 1,
    SelectionBorderWidth: SizingUnit / 2,
    ListRowHeight: 20
  },
  Typography: {
    FontFamily: 'inherit',
    Sizing: FontSizes,
    Color: FontColors
  },
  Opacity: {
    ClearGlass: 0.01,
    Disabled: 0.35,
    OpaqueGlass: 0.33,
  },
};

export const overrideTabStyles = (overridingTabStyles: Styles): void => {
  merge(TabStyles, overridingTabStyles);
};
